import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Key, Trash2, ShieldCheck, Sparkles, ExternalLink, Compass, Languages } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";

/**
 * /settings — personal preferences page.
 *
 * Today this hosts the BYOK (Bring Your Own Key) panel: the user can save
 * a personal provider API key. The key is encrypted at rest with Fernet
 * on the server (master key in backend/.env). The UI never receives the
 * raw key back — only a masked preview like `sk-…XYZ4`.
 *
 * When a personal key is set, every Lesson Builder request will use the
 * user's own quota; otherwise the shared Emergent universal key is used.
 *
 * All user-facing labels go through i18n (`settings_page.*`). Hebrew
 * copy lives in he.json and is byte-identical to what was previously
 * hardcoded, with the single intentional exception of the BYOK panel —
 * brand names (Claude/Anthropic) were neutralised in both languages to
 * comply with the global identity guardrail. The provider link still
 * targets the correct vendor, but the visible link text is generic.
 */
export default function Settings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  const { t } = useTranslation();
  const [keys, setKeys] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  const [draftKey, setDraftKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth?redirect=/settings");
      return;
    }
    api.listApiKeys().then(setKeys).catch(() => setKeys({ keys: [], supported_providers: [] }));
  }, [user, authLoading, navigate]);

  const refresh = async () => {
    try {
      const data = await api.listApiKeys();
      setKeys(data);
    } catch {
      /* ignore */
    }
  };

  const handleSave = async (provider) => {
    if (!draftKey.trim()) return;
    setBusy(true);
    try {
      await api.setApiKey(provider, draftKey.trim());
      // Immediately validate against the upstream provider so the user
      // gets fast green/red feedback rather than discovering 60 s into a
      // lesson build that the key was wrong.
      toast.message(t("settings_page.toast_saving_validating"), { duration: 2000 });
      try {
        const v = await api.validateApiKey(provider);
        if (v?.valid) {
          toast.success(t("settings_page.toast_valid_title"), {
            description: t("settings_page.toast_valid_desc"),
          });
        } else {
          toast.error(t("settings_page.toast_saved_invalid_title"), {
            description: v?.error || t("settings_page.toast_saved_invalid_desc"),
            duration: 8000,
          });
        }
      } catch (vErr) {
        // Validation network blip — non-fatal; key is saved either way.
        toast.message(t("settings_page.toast_saved_title"), {
          description: t("settings_page.toast_saved_no_validation_desc"),
        });
      }
      setDraftKey("");
      setEditingProvider(null);
      await refresh();
    } catch (err) {
      toast.error(t("settings_page.toast_save_error_title"), {
        description: err.response?.data?.detail || t("settings_page.toast_try_again"),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (provider) => {
    if (!window.confirm(t("settings_page.confirm_delete"))) {
      return;
    }
    setBusy(true);
    try {
      await api.deleteApiKey(provider);
      toast.success(t("settings_page.toast_removed"));
      await refresh();
    } catch (err) {
      toast.error(t("settings_page.toast_delete_error_title"), {
        description: err.response?.data?.detail || t("settings_page.toast_try_again"),
      });
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || keys === null) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="mt-6 h-32 bg-muted/50 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div
      className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-14 space-y-10"
      data-testid="settings-page"
    >
      <header className="text-center space-y-3 animate-fade-in-up">
        <span className="font-latin italic text-xs sm:text-sm tracking-widest uppercase text-gold">
          {t("settings_page.eyebrow")}
        </span>
        <h1 className="font-heading text-3xl sm:text-5xl text-foreground">
          {t("settings_page.title")}
        </h1>
        <div className="ornamental-divider mx-auto max-w-md">✦ ❖ ✦</div>
      </header>

      {/* Language preference — first-class section, sized & styled to be
          discoverable for both new users and reviewers (this is what
          our rabbi/proofreaders flip back and forth while validating
          English). Hebrew is the on-app default; the toggle is fully
          bidirectional. */}
      <section
        className="rounded-sm border border-gold/30 bg-card shadow-book p-6 sm:p-8"
        data-testid="settings-language-section"
      >
        <div className="flex items-start gap-3 mb-5">
          <Languages className="h-5 w-5 text-gold mt-1 shrink-0" strokeWidth={1.5} />
          <div>
            <h2 className="font-heading text-xl sm:text-2xl text-foreground">
              {t("settings_page.lang_section_title")}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("settings_page.lang_section_desc")}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setLang("he")}
            data-testid="settings-lang-he"
            dir="rtl"
            className={`group relative rounded-sm border px-5 py-5 text-center transition-all
              ${lang === "he"
                ? "border-gold bg-gold/10 ring-1 ring-gold/40"
                : "border-gold/30 hover:border-gold/60 hover:bg-gold/5"}`}
          >
            <span className="block font-heading text-2xl text-foreground mb-1">עברית</span>
            <span className="block font-latin italic text-[11px] text-muted-foreground tracking-widest uppercase">
              {lang === "he"
                ? t("settings_page.lang_option_hebrew_caption_current")
                : t("settings_page.lang_option_hebrew_caption_idle")}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setLang("en")}
            data-testid="settings-lang-en"
            dir="ltr"
            className={`group relative rounded-sm border px-5 py-5 text-center transition-all
              ${lang === "en"
                ? "border-gold bg-gold/10 ring-1 ring-gold/40"
                : "border-gold/30 hover:border-gold/60 hover:bg-gold/5"}`}
          >
            <span className="block font-heading text-2xl text-foreground mb-1">English</span>
            <span className="block font-latin italic text-[11px] text-muted-foreground tracking-widest uppercase">
              {lang === "en"
                ? t("settings_page.lang_option_english_caption_current")
                : t("settings_page.lang_option_english_caption_idle")}
            </span>
          </button>
        </div>
      </section>

      {/* Advanced — BYOK (personal API key) tucked under a collapsible.
          Most users don't need this; advanced users can expand to plug
          in their own provider key. All visible copy is brand-neutral
          per the global identity guardrail. */}
      <details
        className="rounded-sm border border-border/60 bg-card/40 shadow-book overflow-hidden group"
        data-testid="settings-advanced-section"
      >
        <summary
          className="cursor-pointer select-none px-6 py-4 sm:px-8 sm:py-5 flex items-center justify-between gap-3 list-none [&::-webkit-details-marker]:hidden"
          data-testid="settings-advanced-summary"
        >
          <div className="flex items-center gap-3">
            <Key className="h-5 w-5 text-gold shrink-0" strokeWidth={1.5} />
            <div className="text-right">
              <h2 className="font-heading text-xl sm:text-2xl text-foreground">
                {t("settings_page.advanced_title")}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("settings_page.advanced_subtitle")}
              </p>
            </div>
          </div>
          <span
            className="text-gold transition-transform duration-300 group-open:rotate-180"
            aria-hidden
          >
            ▾
          </span>
        </summary>

        {/* BYOK panel */}
        <section className="px-6 pb-6 sm:px-8 sm:pb-8 space-y-6 border-t border-gold/15 pt-6">
          <div className="flex items-start gap-3">
            <Key className="h-5 w-5 text-gold mt-1 shrink-0" strokeWidth={1.5} />
            <div className="space-y-1">
              <h3 className="font-heading text-lg text-foreground">
                {t("settings_page.byok_panel_title")}{" "}
                <span className="text-sm text-muted-foreground">
                  {t("settings_page.byok_panel_byok_label")}
                </span>
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("settings_page.byok_panel_desc")}
              </p>
              <p className="text-xs text-muted-foreground/80 flex items-center gap-1.5 pt-1">
                <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                {t("settings_page.byok_panel_encryption_note")}
              </p>
            </div>
          </div>

        {keys.keys?.map((row) => {
          const editing = editingProvider === row.provider;

          return (
            <div
              key={row.provider}
              className="rounded-sm border border-border/60 bg-background/50 p-4 sm:p-5 space-y-3"
              data-testid={`api-key-row-${row.provider}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  <span className="font-heading text-base">
                    {t("settings_page.row_provider_personal")}
                  </span>
                  {row.is_set ? (
                    <span
                      className="text-xs px-2 py-0.5 rounded bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20"
                      data-testid={`api-key-status-${row.provider}`}
                    >
                      {t("settings_page.row_status_connected")}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                      {t("settings_page.row_status_unset")}
                    </span>
                  )}
                </div>

                {row.is_set && !editing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(row.provider)}
                    disabled={busy}
                    data-testid={`api-key-delete-${row.provider}`}
                    className="text-destructive hover:bg-destructive/10 gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {t("settings_page.row_remove")}
                  </Button>
                )}
              </div>

              {row.is_set && row.mask && (
                <p className="text-xs font-mono text-muted-foreground" dir="ltr">
                  {row.mask}
                </p>
              )}

              {editing ? (
                <div className="space-y-2">
                  <Input
                    type="password"
                    value={draftKey}
                    onChange={(e) => setDraftKey(e.target.value)}
                    placeholder={t("settings_page.row_input_placeholder")}
                    dir="ltr"
                    className="font-mono text-sm bg-background"
                    data-testid={`api-key-input-${row.provider}`}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSave(row.provider)}
                      disabled={busy || !draftKey.trim()}
                      className="bg-gold text-background hover:bg-gold/90"
                      data-testid={`api-key-save-${row.provider}`}
                    >
                      {t("settings_page.row_save")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingProvider(null);
                        setDraftKey("");
                      }}
                      disabled={busy}
                      data-testid={`api-key-cancel-${row.provider}`}
                    >
                      {t("settings_page.row_cancel")}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant={row.is_set ? "outline" : "default"}
                  size="sm"
                  onClick={() => {
                    setEditingProvider(row.provider);
                    setDraftKey("");
                  }}
                  className={
                    row.is_set
                      ? ""
                      : "bg-gold text-background hover:bg-gold/90"
                  }
                  data-testid={`api-key-edit-${row.provider}`}
                >
                  {row.is_set
                    ? t("settings_page.row_replace")
                    : t("settings_page.row_add")}
                </Button>
              )}

              {!row.is_set && !editing && (
                <a
                  href="https://console.anthropic.com/settings/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-gold hover:underline"
                  data-testid={`api-key-help-${row.provider}`}
                >
                  {t("settings_page.row_help_link")}
                  <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                </a>
              )}
            </div>
          );
        })}
      </section>
      </details>

      <p className="text-center text-xs text-muted-foreground">
        {t("settings_page.shared_balance_note")}
      </p>

      {/* Replay onboarding — for users who want to re-watch the welcome tour. */}
      <section
        className="rounded-sm border border-border/60 bg-card/60 p-5 sm:p-6 space-y-3"
        data-testid="settings-replay-tour-section"
      >
        <div className="flex items-start gap-3">
          <Compass className="h-5 w-5 text-gold mt-1 shrink-0" strokeWidth={1.5} />
          <div className="flex-1 space-y-1">
            <h2 className="font-heading text-lg text-foreground">
              {t("settings_page.tour_section_title")}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("settings_page.tour_section_desc")}
            </p>
          </div>
          <Link to="/onboarding?force=1" data-testid="settings-replay-tour-btn">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-gold/40 hover:border-gold hover:bg-gold/10"
            >
              {t("settings_page.tour_section_btn")}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
